// src/views/Inventory/IncomingPC/FormIncomingPC.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Select,
  Input,
  DatePicker,
  Button,
  Typography,
  Space,
  message,
  Divider,
  Modal,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../../../configs"; // sesuaikan path
import Title from "antd/es/typography/Title";

const { Text } = Typography;
const { Option } = Select;

export default function FormIncomingPC() {
  const [form] = Form.useForm();

  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const [stpcNumber, setStpcNumber] = useState("");
  const [stpcDate] = useState(dayjs()); // tanggal STPC = hari ini

  const [uploadName, setUploadName] = useState(null);
  const [uploadBase64, setUploadBase64] = useState(null);

  const [saving, setSaving] = useState(false);

  // Confirmation Modal State
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  // ---------- Generate STPC Number di FE ----------
  const generateStpcNumber = () => {
    const today = dayjs().format("YYYYMMDD");
    // simple counter lokal per sesi: ambil dari localStorage biar nggak reset tiap reload
    const key = `STPC_SEQ_${today}`;
    let current = parseInt(localStorage.getItem(key) || "0", 10);
    current += 1;
    localStorage.setItem(key, String(current).padStart(3, "0"));
    const seq = String(current).padStart(3, "0");
    return `STP-${today}-${seq}`;
  };

  // ---------- Load contacts (importir) ----------
  const fetchContacts = async () => {
    setLoadingContacts(true);
    try {
      const res = await axiosInstance.get("/contact");
      setContacts(res.data || []);
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat daftar importir");
    } finally {
      setLoadingContacts(false);
    }
  };

  useEffect(() => {
    setStpcNumber(generateStpcNumber());
    fetchContacts();
  }, []);

  // ---------- Watch detail untuk hitung total ----------
  const details = Form.useWatch("details", form);

  const { totalLembar, totalKeping } = useMemo(() => {
    const rows = details || [];
    let tL = 0;
    let tK = 0;
    rows.forEach((r) => {
      const lembar = Number(r?.lembar) || 0;
      const keping = Number(r?.keping) || 0;
      tL += lembar;
      tK += keping;
    });
    return { totalLembar: tL, totalKeping: tK };
  }, [details]);

  // ---------- Upload lampiran ----------
  const handleUploadChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "application/pdf",
    ];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      message.error("Jenis file harus JPG, PNG, GIF, atau PDF");
      return;
    }
    if (file.size > maxSize) {
      message.error("Ukuran file maksimal 5 MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      const base64Content = base64String.split(",")[1];
      setUploadName(file.name);
      setUploadBase64(base64Content);
    };
    reader.readAsDataURL(file);
  };

  // ---------- Submit ----------
  const handleFinish = async (values) => {
    if (!details || details.length === 0) {
      message.error("Minimal 1 baris detail wajib diisi");
      return;
    }

    setPendingValues(values);
    setConfirmVisible(true);
  };

  const handleConfirmSubmit = async () => {
    const values = pendingValues;

    setSaving(true);
    try {
      const payload = {
        header: {
          id_customer: values.id_customer,
          no_ck1a: values.no_ck1a,
          tanggal_terima: values.tanggal_terima
            ? dayjs(values.tanggal_terima).format("YYYY-MM-DD")
            : null,
          stpc_number: stpcNumber,
          stpc_date: stpcDate.format("YYYY-MM-DD"),
          jenis_bkc: values.jenis_bkc,
          total_lembar: totalLembar,
          total_keping: totalKeping,
          lampiran_1: uploadBase64 || null, // kalau BE cuma butuh nama, ganti ke uploadName
          created_by: 0,
          updated_by: 0,
        },
        details: (values.details || []).map((row, idx) => ({
          seri: idx + 1,
          golongan: row.golongan,
          isi: row.isi,
          satuan: row.satuan,
          lembar: Number(row.lembar) || 0,
          keping: Number(row.keping) || 0,
          created_by: 0,
          updated_by: 0,
        })),
      };

      await axiosInstance.post("/incoming-pc", payload);

      message.success("Data STPC berhasil disimpan");
      // optional: reset form & generate nomor baru
      form.resetFields();
      setUploadBase64(null);
      setUploadName(null);
      setStpcNumber(generateStpcNumber());
      setConfirmVisible(false);
    } catch (err) {
      console.error(err);
      message.error("Gagal menyimpan data STPC");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // sesuaikan dengan routing kamu
    window.history.back();
  };

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 4 }}>
            Form Serah Terima Pita Cukai
          </Title>
          <Text type="secondary">
            Kelola Serah Terima PC untuk kebutuhan inventory & kepabeanan.
          </Text>
        </Col>
      </Row>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          tanggal_terima: null,
          details: [
            {
              isi: undefined,
              satuan: undefined,
              lembar: undefined,
              keping: undefined,
            },
          ],
        }}
      >
        {/* ===== HEADER ===== */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Nama Importir</Text>}
              name="id_customer"
              rules={[{ required: true, message: "Pilih nama importir" }]}
            >
              <Select
                placeholder="Pilih nama importir"
                loading={loadingContacts}
                showSearch
                optionFilterProp="children"
              >
                {contacts.map((c) => (
                  <Option key={c.id_contact} value={c.npwp}>
                    {c.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>No CK1A</Text>}
              name="no_ck1a"
              rules={[{ required: true, message: "Isi No CK1A" }]}
            >
              <Input placeholder="Masukkan No CK1A" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Tanggal Diterima</Text>}
              name="tanggal_terima"
              rules={[{ required: true, message: "Pilih tanggal diterima" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD MMMM YYYY"
                placeholder="Pilih tanggal"
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Jenis BKC</Text>}
              name="jenis_bkc"
              rules={[{ required: true, message: "Jenis BKC" }]}
            >
              <Input placeholder="Contoh: MMEA" />
            </Form.Item>
          </Col>
        </Row>

        {/* ===== LAMPIRAN ===== */}
        <Divider style={{ margin: "8px 0 16px" }} />

        <Row gutter={16} align="middle">
          <Col xs={24} md={3}>
            <Text strong>Lampiran 1</Text>
          </Col>
          <Col xs={24} md={21}>
            <Space direction="vertical" size={4}>
              <Button
                icon={<UploadOutlined />}
                type="primary"
                onClick={() => {
                  document.getElementById("upload-stpc-lampiran").click();
                }}
                style={{
                  background: "#FFA800",
                  borderColor: "#FFA800",
                  fontWeight: 600,
                }}
              >
                Upload File
              </Button>
              <input
                id="upload-stpc-lampiran"
                type="file"
                style={{ display: "none" }}
                accept=".jpg,.jpeg,.png,.gif,.pdf"
                onChange={handleUploadChange}
              />
              <Text type="secondary">
                Jenis File: JPG | JPEG | PNG | GIF | PDF. Maks 5 MB.
              </Text>
              {uploadName && (
                <Text type="success">File terpilih: {uploadName}</Text>
              )}
            </Space>
          </Col>
        </Row>

        {/* ===== TABEL DETAIL ===== */}
        <Divider style={{ margin: "16px 0" }} />

        <Card
          bodyStyle={{ padding: 0 }}
          style={{
            borderRadius: 4,
            border: "1px solid #E4E6EF",
            overflow: "hidden",
          }}
        >
          {/* header grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "0.3fr 2fr 2fr 0.7fr 0.7fr 1.2fr 80px",
              background: "#F3F6F9",
              padding: "8px 16px",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            <div>SERI</div>
            <div>GOLONGAN PITA</div>
            <div>ISI</div>
            <div>SATUAN</div>
            <div>LEMBAR</div>
            <div>KEPING</div>
            <div style={{ textAlign: "center" }}>AKSI</div>
          </div>

          <Form.List name="details">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <div key={field.key}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "0.3fr 2fr 2fr 0.7fr 0.7fr 1.2fr 80px",
                        padding: "8px 16px",
                        borderTop: "1px solid #E4E6EF",
                        alignItems: "center",
                      }}
                    >
                      {/* SERI (read only) */}
                      <div style={{ paddingRight: 8 }}>{index + 1}</div>

                      {/* ISI */}
                      <Form.Item
                        {...field}
                        name={[field.name, "golongan"]}
                        fieldKey={[field.fieldKey, "golongan"]}
                        rules={[{ required: true, message: "Isi wajib diisi" }]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input placeholder="Golongan (misal: Pita Cukai A)" />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, "isi"]}
                        fieldKey={[field.fieldKey, "isi"]}
                        rules={[{ required: true, message: "Isi wajib diisi" }]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input placeholder="Isi (misal: 700)" />
                      </Form.Item>

                      {/* SATUAN */}
                      <Form.Item
                        {...field}
                        name={[field.name, "satuan"]}
                        fieldKey={[field.fieldKey, "satuan"]}
                        rules={[
                          {
                            required: true,
                            message: "Satuan wajib diisi",
                          },
                          {
                            max: 5,
                            message: "Maksimal 5 huruf",
                          },
                        ]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input
                          placeholder="ML"
                          maxLength={5}
                          style={{ textTransform: "uppercase" }}
                        />
                      </Form.Item>

                      {/* LEMBAR */}
                      <Form.Item
                        {...field}
                        name={[field.name, "lembar"]}
                        fieldKey={[field.fieldKey, "lembar"]}
                        rules={[
                          {
                            required: true,
                            message: "Isi jumlah lembar",
                          },
                        ]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input
                          inputMode="numeric"
                          placeholder="0"
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^0-9]/g, "");
                            form.setFieldsValue({
                              details: (
                                form.getFieldValue("details") || []
                              ).map((row, i) =>
                                i === index
                                  ? { ...row, lembar: v, keping: v * 60 }
                                  : row,
                              ),
                            });
                          }}
                        />
                      </Form.Item>

                      {/* KEPING */}
                      <Form.Item
                        {...field}
                        name={[field.name, "keping"]}
                        fieldKey={[field.fieldKey, "keping"]}
                        rules={[
                          {
                            required: true,
                            message: "Isi jumlah keping",
                          },
                        ]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input inputMode="numeric" placeholder="0" readOnly />
                      </Form.Item>

                      <div style={{ textAlign: "center" }}>
                        {fields.length > 1 && (
                          <Button
                            type="text"
                            icon={
                              <DeleteOutlined style={{ color: "#F64E60" }} />
                            }
                            onClick={() => remove(field.name)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                <div
                  style={{
                    padding: "8px 16px 12px",
                    borderTop: "1px solid #F3F6F9",
                  }}
                >
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() =>
                      add({
                        golongan: undefined,
                        isi: undefined,
                        satuan: undefined,
                        lembar: undefined,
                        keping: undefined,
                      })
                    }
                  >
                    Tambah Pita
                  </Button>
                </div>
              </>
            )}
          </Form.List>
        </Card>

        {/* ===== TOTAL ===== */}
        <Row justify="end" style={{ marginTop: 16 }}>
          <Col>
            <div style={{ textAlign: "right" }}>
              <div>
                <Text strong>TOTAL LEMBAR :</Text>{" "}
                <Text>{totalLembar} Lembar</Text>
              </div>
              <div>
                <Text strong>TOTAL KEPING :</Text>{" "}
                <Text>{totalKeping} Keping</Text>
              </div>
            </div>
          </Col>
        </Row>

        {/* ===== ACTION BUTTONS ===== */}
        <Row justify="end" style={{ marginTop: 24 }}>
          <Space>
            <Button danger onClick={handleCancel}>
              Batal
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={saving}
              style={{
                background: "#1BC5BD",
                borderColor: "#1BC5BD",
                minWidth: 120,
                fontWeight: 600,
              }}
            >
              Simpan
            </Button>
          </Space>
        </Row>
      </Form>

      {/* Confirmation Modal */}
      <Modal
        title="Konfirmasi Serah Terima Pita Cukai"
        open={confirmVisible}
        onOk={handleConfirmSubmit}
        onCancel={() => setConfirmVisible(false)}
        okText="Ya, Simpan"
        cancelText="Batal"
        confirmLoading={saving}
        width={600}
      >
        {pendingValues && (
          <div>
            <p>Apakah Anda yakin ingin menyimpan data STPC ini?</p>
            <div
              style={{
                background: "#f5f5f5",
                padding: 10,
                borderRadius: 4,
                marginBottom: 16,
              }}
            >
              <p style={{ margin: 0 }}>
                <strong>No STPC:</strong> {stpcNumber}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Tanggal:</strong> {stpcDate.format("DD MMMM YYYY")}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Importir:</strong>{" "}
                {contacts.find((c) => c.npwp === pendingValues.id_customer)
                  ?.name || pendingValues.id_customer}
              </p>
            </div>

            <Text strong>Ringkasan Pita:</Text>
            <div
              style={{
                maxHeight: 200,
                overflowY: "auto",
                marginTop: 8,
                border: "1px solid #f0f0f0",
              }}
            >
              <table
                style={{
                  width: "100%",
                  fontSize: 12,
                  borderCollapse: "collapse",
                }}
              >
                <thead
                  style={{ background: "#fafafa", position: "sticky", top: 0 }}
                >
                  <tr>
                    <th
                      style={{
                        padding: "4px 8px",
                        textAlign: "left",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Golongan
                    </th>
                    <th
                      style={{
                        padding: "4px 8px",
                        textAlign: "right",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Lembar
                    </th>
                    <th
                      style={{
                        padding: "4px 8px",
                        textAlign: "right",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Keping
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(pendingValues.details || []).map((d, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "4px 8px" }}>{d.golongan}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right" }}>
                        {d.lembar}
                      </td>
                      <td style={{ padding: "4px 8px", textAlign: "right" }}>
                        {d.keping}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </Card>
  );
}
